import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input } from 'reactstrap';
import { buildPrintHtml, getPageDimsPx, getInitialPreviewLang } from './templateUtils';

const BRIEFING_MAX = 500;
const THUMB_W = 120;
const THUMB_H = 170;

// Scaled-down render of the template itself (sample values), in the language the canvas opens with
const InspirationThumbnail = ({ template }) => {
    const page = getPageDimsPx(template.pageSize, template.orientation);
    const scale = Math.min(THUMB_W / page.w, THUMB_H / page.h);
    return (
        <div style={{ width: THUMB_W, height: THUMB_H, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <div style={{ width: page.w * scale, height: page.h * scale, overflow: 'hidden', border: '1px solid #dee2e6', background: '#fff' }}>
                <iframe
                    title={template.name}
                    srcDoc={buildPrintHtml(template, template.elements, {}, getInitialPreviewLang())}
                    scrolling="no"
                    tabIndex={-1}
                    style={{ width: page.w, height: page.h, border: 0, transform: `scale(${scale})`, transformOrigin: '0 0', pointerEvents: 'none' }}
                />
            </div>
        </div>
    );
};

/**
 * NewTemplateChoiceModal — shown once when a template is opened from scratch
 * (new, no :id). Lets the user pick page format + orientation and optionally
 * describe a briefing, then choose an AI-generated base layout or a blank start.
 *
 * Props:
 *   isOpen   {boolean}
 *   aiEnabled {boolean} - hotel group has the AI module; without it only the blank start is offered
 *   onBase   {Function} - ({ pageSize, orientation, briefing }) => void
 *   onBlank  {Function} - ({ pageSize, orientation }) => void (also dismiss)
 *   baseTemplates {Array} - base designs (GET voucherTemplateDesign/base) as layouts: { id, name, pageSize, orientation, elements, ... }
 *   onInspiration {Function} - (base) => void; copies a base design (its own format and orientation)
 */
class NewTemplateChoiceModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pageSize: 'A4',
            orientation: 'portrait',
            briefing: '',
        };
    }

    _setPageSize = (pageSize) => this.setState({ pageSize });

    _setOrientation = (orientation) => this.setState({ orientation });

    _setBriefing = (briefing) => this.setState({ briefing });

    _handleBase = () => {
        const { pageSize, orientation, briefing } = this.state;
        this.props.onBase({ pageSize, orientation, briefing });
    };

    _handleBlank = () => {
        const { pageSize, orientation } = this.state;
        this.props.onBlank({ pageSize, orientation });
    };

    render() {
        const { isOpen, aiEnabled } = this.props;
        const { pageSize, orientation, briefing } = this.state;

        return (
            <Modal isOpen={isOpen} toggle={this._handleBlank} fade={false} centered>
                <ModalHeader toggle={this._handleBlank}>
                    <FormattedMessage id="TemplateAssistant.NewTemplate.Title" />
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
                        {aiEnabled && (
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: '#6f42c1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <i className="fa-solid fa-robot" aria-hidden="true" style={{ color: '#fff', fontSize: '1.1rem' }} />
                            </div>
                        )}
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#495057' }}>
                            <FormattedMessage id={aiEnabled ? 'TemplateAssistant.NewTemplate.Intro' : 'TemplateAssistant.NewTemplate.IntroNoAi'} />
                        </p>
                    </div>

                    {/* Format */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateAssistant.NewTemplate.Format" />
                        </Label>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {['A4', 'A5'].map(size => (
                                <Button
                                    key={size}
                                    size="sm"
                                    color={pageSize === size ? 'primary' : 'secondary'}
                                    onClick={() => this._setPageSize(size)}
                                >
                                    <FormattedMessage id={`TemplateAssistant.NewTemplate.${size}`} />
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Orientation */}
                    <div className="mb-3">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateAssistant.NewTemplate.Orientation" />
                        </Label>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <Button
                                size="sm"
                                color={orientation === 'portrait' ? 'primary' : 'secondary'}
                                onClick={() => this._setOrientation('portrait')}
                            >
                                <i className="fa-solid fa-mobile-alt mr-1" aria-hidden="true" />
                                <FormattedMessage id="TemplateAssistant.NewTemplate.Portrait" />
                            </Button>
                            <Button
                                size="sm"
                                color={orientation === 'landscape' ? 'primary' : 'secondary'}
                                onClick={() => this._setOrientation('landscape')}
                            >
                                <i className="fa-solid fa-tablet-alt fa-rotate-90 mr-1" aria-hidden="true" />
                                <FormattedMessage id="TemplateAssistant.NewTemplate.Landscape" />
                            </Button>
                        </div>
                    </div>

                    {/* Briefing (optional) — feeds the AI base layout, so only with the AI module */}
                    {aiEnabled && <div className="mb-1">
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateAssistant.NewTemplate.BriefingLabel" />
                        </Label>
                        <FormattedMessage id="TemplateAssistant.NewTemplate.BriefingPlaceholder">
                            {placeholder => (
                                <Input
                                    type="textarea"
                                    bsSize="sm"
                                    rows={3}
                                    maxLength={BRIEFING_MAX}
                                    value={briefing}
                                    placeholder={placeholder}
                                    onChange={(e) => this._setBriefing(e.target.value)}
                                    style={{ resize: 'none' }}
                                />
                            )}
                        </FormattedMessage>
                        <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#6c757d', marginTop: 2 }}>
                            {briefing.length}/{BRIEFING_MAX}
                        </div>
                    </div>}

                    {/* Base templates — own format and orientation, independent of the AI module */}
                    {this.props.onInspiration && this.props.baseTemplates && this.props.baseTemplates.length > 0 && (
                        <div className="mt-3">
                            <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="TemplateAssistant.NewTemplate.Inspiration" />
                            </Label>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                                {this.props.baseTemplates.map(template => (
                                    <button
                                        key={template.id}
                                        type="button"
                                        onClick={() => this.props.onInspiration(template)}
                                        style={{ flex: 1, border: '1px solid #ced4da', borderRadius: 4, background: '#f8f9fa', padding: 6, cursor: 'pointer', textAlign: 'center' }}
                                    >
                                        <InspirationThumbnail template={template} />
                                        <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: 4 }}>{template.name}</div>
                                        <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                                            {template.pageSize} · <FormattedMessage id={template.orientation === 'landscape' ? 'TemplateAssistant.NewTemplate.Landscape' : 'TemplateAssistant.NewTemplate.Portrait'} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={this._handleBlank}>
                        <i className="fa-solid fa-file mr-1" aria-hidden="true" />
                        <FormattedMessage id="TemplateAssistant.NewTemplate.Blank" />
                    </Button>
                    {aiEnabled && (
                        <Button className="btn-host" onClick={this._handleBase}>
                            <i className="fa-solid fa-robot mr-1" aria-hidden="true" />
                            <FormattedMessage id="TemplateAssistant.NewTemplate.Base" />
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    }
}

export default NewTemplateChoiceModal;
